import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { FULL_QUALITY, PACKAGE_INVENTORY } from '../../../../shared/featureFlags/flagNames';
import { RISK_ISSUE_TYPE } from '../../../../shared/issuesV2';
import { ENTERPRISE_PLATFORM } from '../../../../shared/subscriptions';
import { isFeatureEnabled, useIsFeatureEnabled, useIsQualityEnabled } from '../../../helpers/features';
import useOnMount from '../../../hooks/useOnMount';
import useToggle from '../../../hooks/useToggle';
import { put } from '../../../redux/api';
import PolicyStore from '../../../redux/policies';
import Button from '../../generic/Button';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Loader from '../../generic/Loader';
import NotFound from '../../generic/NotFound';
import Text from '../../generic/Text';
import ModalAddDependencyFilterRule from '../../modals/ModalAddDependencyFilterRule';
import EditPolicyCTA from '../EditPolicyCTA';
import type { DependencyFilterVersionType, QualityRuleType } from '../../../../shared/model-typings';
import type { ReduxStore } from '../../../redux/store';

import './DisplayQualityPolicyPage.less';

import type { AsyncStatus, Policy, QualityRule, RouterProps } from '../../../types';

import ListRulesComponent from './ListRulesComponent';
import OutdatedRulesComponent from './OutdatedRulesComponent';
import RiskRuleComponent from './RiskRuleComponent';
import type { OutdatedRule, RuleArray, RuleModalType } from './types';

type DisplayQualityPolicyPageProps = RouterProps<{
  policyId: string;
}>;
type DisplayQualityPolicyPageMainContentsProps = {
  policy: Policy;
  status: AsyncStatus;
};

const DEFAULT_OUTDATED_RULE: OutdatedRule = {
  type: 'outdated_dependency',
  enabled: false,
  dependencyOutdatedVersionPart: 'MAJOR',
  dependencyOutdatedVersionDiff: 2, // default to 2
  dependencyOutdatedType: 'SEMVER',
  changed: false,
};

const DEFAULT_OUTDATED_FALLBACK_RULE: OutdatedRule = {
  type: 'outdated_dependency',
  enabled: false,
  dependencyOutdatedVersionDiff: 10, // default to 10
  dependencyOutdatedType: 'FALLBACK',
  changed: false,
};

const DisplayQualityPolicyPageMainContents: VoidFunctionComponent<DisplayQualityPolicyPageMainContentsProps> = ({
  policy,
  status,
}) => {
  // Setup Redux state
  const dispatch = useDispatch();

  // Setup local state
  const [hasChanged, setChanged] = useState(false);
  const [isShowingSuccess, onToggleShowSuccess] = useToggle();
  const [isShowingAddRuleModal, onToggleShowAddRuleModal] = useToggle();
  const [addRuleModalType, setRuleModalType] = useState<RuleModalType>('whitelisted_dependency');
  const [outdatedRule, setOutdatedRule] = useState<OutdatedRule>(DEFAULT_OUTDATED_RULE);
  const [outdatedFallbackRule, setOutdatedFallbackRule] = useState<OutdatedRule>(DEFAULT_OUTDATED_FALLBACK_RULE);
  const [allowRules, setAllowRules] = useState<RuleArray>([]);
  const [denyRules, setDenyRules] = useState<RuleArray>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Risk Rules
  const [isAbandonwareEnabled, setAbandonware] = useState(false);
  const [isEmptyPackageEnabled, setEmptyPackage] = useState(false);
  const [isNativeCodeEnabled, setNativeCode] = useState(false);

  const isQualityRestricted = !useIsFeatureEnabled(FULL_QUALITY);

  /**
   * componentWillReceiveProps equivalent
   * Because policy is a dependency, we know that something about it has changed, making an update necessary.
   */
  useEffect(() => {
    const ruleGroups = _.groupBy(policy.rules, 'type');
    let newOutdatedRule = outdatedRule;
    let newOutdatedFallbackRule = outdatedFallbackRule;
    let newAllowlistRules: RuleArray = [];
    let newBlocklistRules: RuleArray = [];

    if (ruleGroups.outdated_dependency) {
      const outdatedRuleToApply: QualityRule = _.find(ruleGroups.outdated_dependency, [
        'dependencyOutdatedType',
        'SEMVER',
      ]) as QualityRule;
      const outdatedFallbackRuleToApply: QualityRule = _.find(ruleGroups.outdated_dependency, [
        'dependencyOutdatedType',
        'FALLBACK',
      ]) as QualityRule;
      newOutdatedRule = { ...outdatedRuleToApply, changed: false };
      if (outdatedFallbackRuleToApply) {
        newOutdatedFallbackRule = { ...outdatedFallbackRuleToApply, changed: false };
      }
    }

    if (ruleGroups.whitelisted_dependency) {
      newAllowlistRules = [...ruleGroups.whitelisted_dependency] as RuleArray;
    }
    if (ruleGroups.blacklisted_dependency) {
      newBlocklistRules = [...ruleGroups.blacklisted_dependency] as RuleArray;
    }

    if (ruleGroups[RISK_ISSUE_TYPE.RiskAbandonware]) {
      setAbandonware(true);
    }
    if (ruleGroups[RISK_ISSUE_TYPE.RiskEmptyPackage]) {
      setEmptyPackage(true);
    }
    if (ruleGroups[RISK_ISSUE_TYPE.RiskNativeCode]) {
      setNativeCode(true);
    }

    //  This guards against a rare edge case where users may have had the
    //  PACKAGE_INVENTORY flag enabled, created package rules, and then later
    //  had the flag turned off.
    //  A rule is considered a package rule if it has a packageProjectLocator.
    if (!isFeatureEnabled(PACKAGE_INVENTORY)) {
      newBlocklistRules = newBlocklistRules.filter((rule) => !rule.packageProjectLocator);
    }

    setOutdatedRule(newOutdatedRule);
    setOutdatedFallbackRule(newOutdatedFallbackRule);
    setAllowRules(newAllowlistRules);
    setDenyRules(newBlocklistRules);
    // We don't want to include outdatedDependency and outdatedFallbackDependency as deps because it will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy, dispatch, status]);

  /**
   * Consolidate and save policies through Redux
   */
  const onSavePolicy = useCallback(async () => {
    if (!policy) {
      return;
    }

    const aggregatedRules: RuleArray = [...allowRules, ...denyRules, { ...outdatedRule }, { ...outdatedFallbackRule }];

    if (isAbandonwareEnabled) {
      aggregatedRules.push({
        type: RISK_ISSUE_TYPE.RiskAbandonware,
        abandonedMonthsCutoff: 24,
      });
    }

    if (isEmptyPackageEnabled) {
      aggregatedRules.push({
        type: RISK_ISSUE_TYPE.RiskEmptyPackage,
      });
    }

    if (isNativeCodeEnabled) {
      aggregatedRules.push({
        type: RISK_ISSUE_TYPE.RiskNativeCode,
        deniedExtensions: [],
      });
    }

    try {
      await put(`/api/policies/${policy.id}/rules`, aggregatedRules);
      setChanged(false);
      onToggleShowSuccess();
      setErrors([]);
    } catch (err) {
      const message = err.data.message as string;
      setErrors([message]);
    }
  }, [
    denyRules,
    allowRules,
    onToggleShowSuccess,
    outdatedFallbackRule,
    outdatedRule,
    policy,
    isAbandonwareEnabled,
    isNativeCodeEnabled,
    isEmptyPackageEnabled,
  ]);

  /**
   * Adds a rule to the appropriate list.
   */
  const onAddFilterRule = useCallback(
    ({
      name,
      startVersion,
      endVersion,
      versionType,
    }: {
      name: string;
      startVersion: string;
      endVersion?: string;
      versionType: DependencyFilterVersionType;
    }) => {
      const stateList = addRuleModalType === 'blacklisted_dependency' ? denyRules : allowRules;
      const setStateListFn = addRuleModalType === 'blacklisted_dependency' ? setDenyRules : setAllowRules;
      const newRule: Partial<QualityRule> = {
        type: addRuleModalType,
        dependencyFilterName: name,
        dependencyFilterStartVersion: startVersion,
        dependencyFilterEndVersion: endVersion,
        dependencyFilterVersionType: versionType,
        enabled: true,
      };
      const newRuleList = [...stateList, newRule];
      setStateListFn(newRuleList);
      setChanged(true);
    },
    [addRuleModalType, denyRules, allowRules],
  );

  /**
   * Removes a rule at a given index from the appropriate list.
   */
  const onRemoveFilterRule = useCallback(
    (filterType: QualityRuleType, listIndex: number) => {
      const stateList = filterType === 'blacklisted_dependency' ? denyRules.slice() : allowRules.slice();
      const setStateListFn = filterType === 'blacklisted_dependency' ? setDenyRules : setAllowRules;
      stateList.splice(listIndex, 1);
      setStateListFn(stateList);
      setChanged(true);
    },
    [denyRules, allowRules],
  );

  return (
    <>
      <EditPolicyCTA policy={policy} status={status} />
      <div className="margin-top-lg quality-policy-container" style={{ fontSize: '18px' }}>
        <Helmet title={status.isLoading ? 'Policy...' : `${policy.title} - Policies`} />

        <div className="container card">

          {isShowingSuccess && (
            <Alert bsStyle="success" onDismiss={onToggleShowSuccess}>
              Quality rule(s) successfully saved!
            </Alert>
          )}
          {errors.length > 0 && errors.map((err: string) => <div className="alert alert-error">{err}</div>)}

          <div className="header-container">
            {/* Group elements for flex container */}
            <div>
              <Text.H1>Quality Policy</Text.H1>
              <p>Define rules based on the version and/or name of your dependencies</p>
            </div>
            <div>
              <Button type="primary" size="large" isDisabled={!hasChanged} onClick={onSavePolicy}>
                Save
              </Button>
            </div>
          </div>
          {status.loading ? (
            <Loader />
          ) : (
            <div>
              <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
                <OutdatedRulesComponent
                  outdatedRule={outdatedRule}
                  setOutdatedRule={setOutdatedRule}
                  outdatedFallbackRule={outdatedFallbackRule}
                  setOutdatedFallbackRule={setOutdatedFallbackRule}
                  setChanged={setChanged}
                />
              </PaidFeatureGate>
              <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
                <ListRulesComponent
                  title="Allow Rules"
                  description="Selected dependencies will never raise an issue -- even when in violation of another policy."
                  onAddRule={() => {
                    setRuleModalType('whitelisted_dependency');
                    onToggleShowAddRuleModal();
                  }}
                  onRemoveFilterRule={_.partial(onRemoveFilterRule, 'whitelisted_dependency')}
                  rulesList={allowRules}
                />
              </PaidFeatureGate>
              <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
                <ListRulesComponent
                  title="Block Rules"
                  description="Selected dependencies will always raise an issue."
                  onAddRule={() => {
                    setRuleModalType('blacklisted_dependency');
                    onToggleShowAddRuleModal();
                  }}
                  onRemoveFilterRule={_.partial(onRemoveFilterRule, 'blacklisted_dependency')}
                  rulesList={denyRules}
                />
              </PaidFeatureGate>
              <div className="padding-left-md" style={{ marginTop: '50px' }}>
                <Text.H2>Risk Intelligence Rules</Text.H2>
              </div>
              <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
                <RiskRuleComponent
                  title="Abandonware"
                  description="Dependencies that haven’t received updates from their maintainers in +2 years are considered abandoned."
                  supportedFetchers={['npm', 'mvn', 'pip']}
                  isEnabled={isAbandonwareEnabled}
                  onToggle={() => {
                    setChanged(true);
                    setAbandonware(!isAbandonwareEnabled);
                  }}
                />
              </PaidFeatureGate>
              <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
                <RiskRuleComponent
                  title="Empty Package"
                  description="Package has no code and should not be used."
                  supportedFetchers={['npm', 'mvn', 'pip']}
                  isEnabled={isEmptyPackageEnabled}
                  onToggle={() => {
                    setChanged(true);
                    setEmptyPackage(!isEmptyPackageEnabled);
                  }}
                />
              </PaidFeatureGate>
              <PaidFeatureGate isRestricted={isQualityRestricted} planToShow={ENTERPRISE_PLATFORM}>
                <RiskRuleComponent
                  title="Native Code Detection"
                  description="Detects compiled executables that are not expected within an ecosystem/language."
                  supportedFetchers={['npm', 'pip']}
                  isEnabled={isNativeCodeEnabled}
                  onToggle={() => {
                    setChanged(true);
                    setNativeCode(!isNativeCodeEnabled);
                  }}
                />
              </PaidFeatureGate>
            </div>
          )}
        </div>
        <ModalAddDependencyFilterRule
          show={isShowingAddRuleModal}
          onHide={onToggleShowAddRuleModal}
          onAddRule={onAddFilterRule}
          ruleType={addRuleModalType}
        />
      </div>
    </>
  );
};

const DisplayQualityPolicyPage: VoidFunctionComponent<DisplayQualityPolicyPageProps> = (props) => {
  const dispatch = useDispatch();
  const policy = useSelector((state: ReduxStore) => state.policies.policies[props.params.policyId]);
  const status = useSelector(PolicyStore.selectors.getStatus);

  const hasQuality = useIsQualityEnabled();

  useOnMount(() => {
    dispatch(PolicyStore.actions.fetchOne(Number(props.params.policyId)));
  });

  if (!hasQuality || _.isNil(props.params.policyId)) return <NotFound />;
  if (!policy) return <Loader />;

  return <DisplayQualityPolicyPageMainContents policy={policy} status={status} />;
};

export default DisplayQualityPolicyPage;
